import { getRedeemStats, getRedeemTransactionDocumentAPI, getRedeemTransactionsAPI, updateRedeemTransactionAPI } from "@src/api/redeem-transactions";
import { usePartnerDropdownContext } from "@src/context/PartnerDropdown";
import { GetRedeemTransactionsAPIParams, UseRedeemTransactionsServiceOperators } from "@src/lib/types/redeem-transactions";
import { getCurrentMonthDays } from "@src/utils/current-month";
import { downloadExcel } from "@src/utils/download-helper";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import _reduce from "lodash/reduce";
import moment from "moment";
import { useRedeemTransactionsContext } from "./provider";
import numeral from "numeral";

export const useRedeemTransactionsService = 
(args?: any): Readonly<UseRedeemTransactionsServiceOperators> => {
	const queryClient = useQueryClient();
	const { allowFetchStats } = args || {};
	
	const { state, handlePageChange } = useRedeemTransactionsContext();
	const { state: { selectedPartnerId } } = usePartnerDropdownContext();
	const {
			page,
			debouncedSearch,
			rowsPerPage,
			order,
			orderBy,
			selected,
			fromDate,
			toDate
	} = state || {};
	const newPage = (page || 0) + 1;

	const redeemTransactionsListQueryKey = [
		'redeem-transactions',
		{
			newPage,
			debouncedSearch,
			rowsPerPage,
			order,
			orderBy,
			fromDate,
			toDate,
			selectedPartnerId
		}
	];

	const userDocumentQueryKey = [
		'redeem-userdocument',
		{ 
			userId: selected?.userID,
			newPage,
			debouncedSearch,
			rowsPerPage,
			order,
			orderBy,
			fromDate,
			toDate,
			selectedPartnerId
		}
	];

	const { data, refetch, isRefetching: isRefreshing, isLoading } = useQuery({
		queryKey: redeemTransactionsListQueryKey,
		queryFn: async () => {
			const { fromDate: startDate, toDate: endDate } = getCurrentMonthDays();

			const reqParams: GetRedeemTransactionsAPIParams = {
				size: rowsPerPage,
				page: newPage,
				partnerId: selectedPartnerId,
				startDate: startDate,
        endDate: endDate,
			};

			if (debouncedSearch) reqParams.search = debouncedSearch;
			if (orderBy && order) reqParams.sort = `${orderBy},${order}`;
			if (fromDate) reqParams.startDate = fromDate;
			if (toDate) reqParams.endDate = toDate;

			const result = await getRedeemTransactionsAPI(reqParams);

			return result;
		},
	});

	const { data: userDocumentData, isLoading: isLoadingUserDocuments } = useQuery({
		queryKey: userDocumentQueryKey,
		queryFn: async () => {
			if (selected?.userID) {
				const result = await getRedeemTransactionDocumentAPI(selected?.userID);
				return result;
			}
		},
		enabled: !!selected?.userID
	});

	const { data: redeemStats } = useQuery({
		queryKey: userDocumentQueryKey,
		queryFn: async () => {
			if (allowFetchStats) {
				const { fromDate: startDate, toDate: endDate } = getCurrentMonthDays();
				const reqParams: GetRedeemTransactionsAPIParams = {
					size: rowsPerPage,
					page: newPage,
					partnerID: selectedPartnerId,
					startDate: startDate,
        	endDate: endDate,
				};

			if (orderBy && order) reqParams.sort = `${orderBy},${order}`;
			if (fromDate) reqParams.startDate = fromDate;
			if (toDate) reqParams.endDate = toDate;

				const result = await getRedeemStats(reqParams);
				return result;
			}
		},
		enabled: allowFetchStats
	});

	const onRefreshRedeemTransactionsList = () => {
			handlePageChange(null, 0);
			refetch();
	};

	const onExportRedeemTransactionsList = useMutation({
		mutationKey: ["export-redeem-transactions-list"],
		mutationFn: async () => {
			const { fromDate: startDate, toDate: endDate } = getCurrentMonthDays();
			const reqParams: GetRedeemTransactionsAPIParams = {
				startDate: startDate,
        endDate: endDate,
				partnerId: selectedPartnerId,
			};

			if (debouncedSearch) reqParams.search = debouncedSearch;
			if (orderBy && order) reqParams.sort = `${orderBy},${order}`;
			if (fromDate) reqParams.startDate = fromDate;
			if (toDate) reqParams.endDate = toDate;

			const res = await getRedeemTransactionsAPI(reqParams);

			const newJson = _reduce(
				res.items,
				(result: any, value: any, key) => {
					result.push({
						'User ID': value.user?.id,
						'User Name': value.user?.displayName,
						'Draw Number': value.raffle?.code,
						'Ticket Amount': numeral(value.numTickets).format("0,000"),
						'PG Amount': numeral(value.spendAmount).format("0,000.00"),
						'Transaction Date': moment.parseZone(value.redeemedAt).format("MM-DD-YYYY hh:mm A"),
					});

					return result;
				},
				[]
			);

			downloadExcel(newJson, "export-redeem-transactions");
		}
	});

	const onUpdateRedeemTransaction = useMutation({
		mutationKey: ['update-redeem-transaction'],
		mutationFn: async (formData: any) => {
			const res = await updateRedeemTransactionAPI(formData);
			return res;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: redeemTransactionsListQueryKey });
		}
	});

	return {
		data,
		isLoading,
		redeemStats,
		isRefreshing,
		userDocumentData,
		isLoadingUserDocuments,
		onUpdateRedeemTransaction,
		onExportRedeemTransactionsList,
		onRefreshRedeemTransactionsList,
		redeemTransactions: data?.items || [],
	};
}